import React, { useState, useRef, useEffect } from 'react';
import { CoupleSettings, SongTrack } from '../../types';
import { musicPlayer } from '../../utils/musicPlayer';
import {
  saveAudioFileTrack,
  saveUrlSongTrack,
  deleteMusicTrackFromDb,
  getPlayableAudioUrl,
  subscribeToMusicTracks,
} from '../../services/musicDb';
import {
  Music,
  Upload,
  Link as LinkIcon,
  Play,
  Pause,
  Trash2,
  Check,
  Star,
  Volume2,
  Sparkles,
  Heart,
  FileAudio,
  AlertCircle,
  Plus,
  RefreshCw,
  Clock,
  Loader2,
  Radio,
  ListMusic,
} from 'lucide-react';

interface MusicAdminSectionProps {
  settings: CoupleSettings;
  activeAdminProfile?: 'novio' | 'novia';
  onSaveSettings: (newSettings: CoupleSettings) => void;
}

export const MusicAdminSection: React.FC<MusicAdminSectionProps> = ({
  settings,
  activeAdminProfile = 'novio',
  onSaveSettings,
}) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'url'>('upload');
  const [playingTrackId, setPlayingTrackId] = useState<string | null>(null);
  const [previewAudio, setPreviewAudio] = useState<HTMLAudioElement | null>(null);
  const [previewCurrentTime, setPreviewCurrentTime] = useState<number>(0);
  const [previewDuration, setPreviewDuration] = useState<number>(0);

  // Form states for adding a song
  const [songTitle, setSongTitle] = useState('');
  const [songArtist, setSongArtist] = useState('');
  const [songUrl, setSongUrl] = useState('');
  const [songDuration, setSongDuration] = useState('');
  const [songNotes, setSongNotes] = useState('');
  const [uploader, setUploader] = useState<'novio' | 'novia'>(activeAdminProfile);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [fileError, setFileError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Real-time track list synced from Firestore
  const [cloudTracks, setCloudTracks] = useState<SongTrack[]>(() => {
    const raw = settings.playlist || [];
    return raw.filter((t) => !t.isDefault && !['track-1', 'track-2', 'track-3', 'track-4'].includes(t.id));
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Real-time subscription to music_tracks collection
  useEffect(() => {
    const unsub = subscribeToMusicTracks((tracks) => {
      setCloudTracks(tracks || []);
    });
    return () => unsub();
  }, []);

  const playlist = cloudTracks;
  const activeSongId = settings.activeSongId || (playlist.length > 0 ? playlist[0].id : undefined);

  // Clean up preview audio on unmount
  useEffect(() => {
    return () => {
      if (previewAudio) {
        previewAudio.pause();
        previewAudio.src = '';
      }
    };
  }, [previewAudio]);

  // Handle local preview playback in admin
  const handleTogglePreview = async (track: SongTrack) => {
    if (playingTrackId === track.id) {
      if (previewAudio) {
        previewAudio.pause();
      }
      setPlayingTrackId(null);
      setPreviewCurrentTime(0);
      setPreviewDuration(0);
      return;
    }

    if (previewAudio) {
      previewAudio.pause();
      previewAudio.src = '';
    }
    setPreviewCurrentTime(0);
    setPreviewDuration(0);

    if (track.url === 'synth://pentatonic-ambient') {
      musicPlayer.playTrackById(track.id);
      setPlayingTrackId(track.id);
      return;
    }

    try {
      setPlayingTrackId(track.id);
      const resolvedUrl = await getPlayableAudioUrl(track);
      if (!resolvedUrl) {
        setFileError('No se pudo cargar la melodía para previsualizar.');
        setPlayingTrackId(null);
        return;
      }

      const audio = new Audio(resolvedUrl);
      audio.volume = settings.musicVolume ?? 0.7;
      audio.onended = () => {
        setPlayingTrackId(null);
        setPreviewCurrentTime(0);
      };
      audio.onerror = () => {
        setFileError('No se pudo reproducir este audio. Verifica el formato o conexión.');
        setPlayingTrackId(null);
        setPreviewCurrentTime(0);
      };
      audio.ontimeupdate = () => {
        setPreviewCurrentTime(audio.currentTime);
      };
      audio.onloadedmetadata = () => {
        if (audio.duration && !isNaN(audio.duration)) {
          setPreviewDuration(audio.duration);
        }
      };

      await audio.play();
      setPreviewAudio(audio);
    } catch (e) {
      console.warn('Cannot preview audio:', e);
      setPlayingTrackId(null);
    }
  };

  const handleSeekPreview = (targetSeconds: number) => {
    if (previewAudio) {
      previewAudio.currentTime = targetSeconds;
      setPreviewCurrentTime(targetSeconds);
    }
  };

  // Helper to format seconds to M:SS
  const formatSeconds = (sec: number) => {
    const mins = Math.floor(sec / 60);
    const secs = Math.floor(sec % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Clean filename to make a good default title
  const cleanFilenameToTitle = (filename: string): string => {
    return filename
      .replace(/\.[^/.]+$/, '')
      .replace(/[-_]+/g, ' ')
      .replace(/^\d+[\s.-]*/, '')
      .trim();
  };

  // Handle Audio File Selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    const validExts = /\.(mp3|wav|ogg|m4a|aac|flac)$/i;
    if (!file.type.startsWith('audio/') && !file.name.match(validExts)) {
      setFileError('Por favor selecciona un archivo de audio válido (.mp3, .m4a, .wav, .ogg).');
      return;
    }

    // Size limit notice (< 15MB)
    if (file.size > 15 * 1024 * 1024) {
      setFileError('El archivo es mayor a 15 MB. Te recomendamos un archivo MP3 comprimido para una carga más rápida.');
      return;
    }

    setFileError(null);
    setSelectedFile(file);
    setIsProcessingFile(true);

    if (!songTitle) {
      setSongTitle(cleanFilenameToTitle(file.name));
    }
    if (!songArtist) {
      setSongArtist(uploader === 'novio' ? (settings.partner1Name || 'Novio') : (settings.partner2Name || 'Novia'));
    }

    // Read audio duration using temporary ObjectURL
    try {
      const tempUrl = URL.createObjectURL(file);
      const tempAudio = new Audio(tempUrl);
      tempAudio.addEventListener('loadedmetadata', () => {
        if (tempAudio.duration && !isNaN(tempAudio.duration)) {
          setSongDuration(formatSeconds(tempAudio.duration));
        }
        URL.revokeObjectURL(tempUrl);
      });
      tempAudio.addEventListener('error', () => {
        URL.revokeObjectURL(tempUrl);
      });
    } catch {
      // ignore
    }

    setIsProcessingFile(false);
  };

  // Handle Add Song (Cloud Firestore Chunking + IndexedDB or Direct URL)
  const handleAddSong = async (e: React.FormEvent) => {
    e.preventDefault();
    setFileError(null);

    if (!songTitle.trim()) {
      setFileError('Por favor introduce un título para la canción.');
      return;
    }

    if (activeTab === 'upload') {
      if (!selectedFile) {
        setFileError('Por favor selecciona un archivo de música de tu dispositivo.');
        return;
      }

      try {
        setIsUploading(true);
        setUploadProgress(5);

        // Upload and chunk to Firestore subcollection + IndexedDB
        const newTrack = await saveAudioFileTrack(
          selectedFile,
          {
            title: songTitle.trim(),
            artist: songArtist.trim() || (uploader === 'novio' ? (settings.partner1Name || 'Novio') : (settings.partner2Name || 'Novia')),
            duration: songDuration.trim() || '3:30',
            uploadedBy: uploader,
            notes: songNotes.trim() || undefined,
          },
          (progress) => {
            setUploadProgress(progress);
          }
        );

        // Update local list optimistically
        const updatedList = [...playlist, newTrack];
        setCloudTracks(updatedList);

        // Save lightweight settings document (no heavy data URLs)
        const newSettings: CoupleSettings = {
          ...settings,
          playlist: updatedList.map((t) => ({
            ...t,
            url: t.storageType === 'cloud_chunks' ? '' : t.url,
          })),
          activeSongId: settings.activeSongId || newTrack.id,
          musicEnabled: true,
        };

        onSaveSettings(newSettings);
        musicPlayer.setPlaylist(updatedList, newSettings.activeSongId);

        // Reset form
        setSongTitle('');
        setSongArtist('');
        setSongUrl('');
        setSongDuration('');
        setSongNotes('');
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';

        setSuccessMessage(`¡"${newTrack.title}" fue subida y sincronizada en tiempo real con éxito!`);
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (err) {
        console.error('Error uploading track:', err);
        setFileError('Ocurrió un error al guardar la canción. Por favor intenta de nuevo.');
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    } else {
      // URL Tab
      if (!songUrl.trim()) {
        setFileError('Por favor introduce una URL válida del archivo de audio.');
        return;
      }

      try {
        setIsUploading(true);
        const newTrack = await saveUrlSongTrack({
          title: songTitle.trim(),
          artist: songArtist.trim() || (uploader === 'novio' ? settings.partner1Name : settings.partner2Name) || 'Nuestra Canción',
          url: songUrl.trim(),
          duration: songDuration.trim() || '3:30',
          uploadedBy: uploader,
          notes: songNotes.trim() || undefined,
        });

        const updatedList = [...playlist, newTrack];
        setCloudTracks(updatedList);

        const newSettings: CoupleSettings = {
          ...settings,
          playlist: updatedList,
          activeSongId: settings.activeSongId || newTrack.id,
          musicEnabled: true,
        };

        onSaveSettings(newSettings);
        musicPlayer.setPlaylist(updatedList, newSettings.activeSongId);

        setSongTitle('');
        setSongArtist('');
        setSongUrl('');
        setSongDuration('');
        setSongNotes('');
        setSuccessMessage(`¡"${newTrack.title}" fue añadida a la playlist!`);
        setTimeout(() => setSuccessMessage(null), 4000);
      } catch (err) {
        console.error('Error saving URL track:', err);
        setFileError('Error al guardar el enlace de audio.');
      } finally {
        setIsUploading(false);
      }
    }
  };

  // Handle Set Active Song
  const handleSetActiveSong = (trackId: string) => {
    const newSettings: CoupleSettings = {
      ...settings,
      activeSongId: trackId,
      musicEnabled: true,
    };
    onSaveSettings(newSettings);
    musicPlayer.setPlaylist(playlist, trackId);
    musicPlayer.playTrackById(trackId);
    setSuccessMessage('Canción principal actualizada. Ya está sonando en vivo en la web.');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Handle Delete Song
  const handleDeleteSong = async (trackId: string) => {
    const trackToDelete = playlist.find((t) => t.id === trackId);
    if (!confirm(`¿Eliminar "${trackToDelete?.title || 'esta canción'}" de la playlist?`)) {
      return;
    }

    try {
      await deleteMusicTrackFromDb(trackId);

      const updatedPlaylist = playlist.filter((t) => t.id !== trackId);
      setCloudTracks(updatedPlaylist);

      let nextActiveId = settings.activeSongId;
      if (settings.activeSongId === trackId) {
        nextActiveId = updatedPlaylist.length > 0 ? updatedPlaylist[0].id : undefined;
      }

      const newSettings: CoupleSettings = {
        ...settings,
        playlist: updatedPlaylist.map((t) => ({
          ...t,
          url: t.storageType === 'cloud_chunks' ? '' : t.url,
        })),
        activeSongId: nextActiveId,
      };

      onSaveSettings(newSettings);
      musicPlayer.setPlaylist(updatedPlaylist, nextActiveId);

      if (playingTrackId === trackId && previewAudio) {
        previewAudio.pause();
        setPlayingTrackId(null);
      }

      setSuccessMessage('Canción eliminada correctamente.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error deleting track:', err);
      setFileError('No se pudo eliminar la canción.');
    }
  };

  // Handle Settings Change
  const handleVolumeChange = (newVolume: number) => {
    const updated: CoupleSettings = {
      ...settings,
      musicVolume: newVolume,
    };
    onSaveSettings(updated);
    musicPlayer.setVolume(newVolume);
  };

  const handleLoopModeChange = (mode: 'all' | 'one' | 'none') => {
    const updated: CoupleSettings = {
      ...settings,
      musicLoopMode: mode,
    };
    onSaveSettings(updated);
    musicPlayer.setLoopMode(mode);
  };

  return (
    <div className="space-y-6 animate-fade-in" id="admin-music-section">
      {/* 1. Header Banner */}
      <div className="bg-gradient-to-r from-amber-50 via-rose-50 to-purple-50 border border-amber-200 rounded-2xl p-5 sm:p-6 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-300 flex items-center justify-center text-amber-600 shrink-0 shadow-xs">
              <Music className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
                <span>Gestión de Música & Playlist</span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                  {playlist.length} {playlist.length === 1 ? 'canción' : 'canciones'}
                </span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
                Sube archivos de audio desde tu dispositivo o agrega URLs. Se guardan en la nube permanentemente y suenan en tiempo real.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => {
                if (activeSongId) {
                  musicPlayer.playTrackById(activeSongId);
                } else if (playlist.length > 0) {
                  musicPlayer.playTrackById(playlist[0].id);
                }
              }}
              className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
            >
              <Play className="w-3.5 h-3.5 fill-current" />
              <span>Reproducir en la Web</span>
            </button>
          </div>
        </div>
      </div>

      {/* Success Notification */}
      {successMessage && (
        <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium flex items-center gap-2 shadow-2xs">
          <Check className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Error Notification */}
      {fileError && (
        <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-medium flex items-center gap-2 shadow-2xs">
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
          <span>{fileError}</span>
        </div>
      )}

      {/* 2. Grid: Add New Music & General Settings */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Form to Upload / Add Song */}
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-5 shadow-xs">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Plus className="w-4 h-4 text-amber-500" />
              <span>Añadir Nueva Canción a la Web</span>
            </h3>

            {/* Switch Upload vs URL */}
            <div className="flex items-center p-0.5 rounded-xl bg-slate-100 border border-slate-200">
              <button
                type="button"
                onClick={() => {
                  setActiveTab('upload');
                  setFileError(null);
                }}
                className={`px-3 py-1 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                  activeTab === 'upload'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                <Upload className="w-3 h-3" />
                <span>Subir Archivo</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveTab('url');
                  setFileError(null);
                }}
                className={`px-3 py-1 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                  activeTab === 'url'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                <LinkIcon className="w-3 h-3" />
                <span>Enlace URL</span>
              </button>
            </div>
          </div>

          <form onSubmit={handleAddSong} className="space-y-4">
            {/* File Upload Box */}
            {activeTab === 'upload' ? (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
                  onChange={handleFileChange}
                  className="hidden"
                  id="admin-audio-file-input"
                />

                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all ${
                    selectedFile
                      ? 'border-amber-400 bg-amber-50/50'
                      : 'border-slate-200 bg-slate-50/50 hover:border-amber-400 hover:bg-amber-50/20'
                  }`}
                >
                  <div className="flex flex-col items-center justify-center gap-2">
                    <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center shadow-2xs">
                      <FileAudio className="w-6 h-6" />
                    </div>
                    {selectedFile ? (
                      <div>
                        <p className="text-xs font-bold text-slate-900">
                          {selectedFile.name}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB • Haz clic para cambiar de archivo
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs font-bold text-slate-800">
                          Selecciona una canción de tu celular o computadora
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Soporta formatos MP3, M4A, WAV, OGG (hasta 15 MB)
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Upload Progress Bar */}
                {isUploading && (
                  <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 space-y-1.5 shadow-2xs">
                    <div className="flex items-center justify-between text-xs font-semibold text-amber-800">
                      <span className="flex items-center gap-1.5">
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-600" />
                        <span>Sincronizando y guardando en la nube...</span>
                      </span>
                      <span className="font-mono font-bold">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-amber-100 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-amber-500 h-full transition-all duration-200 rounded-full"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Enlace directo de audio (.mp3, .wav o streaming)
                </label>
                <div className="relative">
                  <input
                    type="url"
                    placeholder="https://servidor.com/cancion.mp3"
                    value={songUrl}
                    onChange={(e) => setSongUrl(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-xs rounded-xl bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-amber-400 focus:outline-hidden text-slate-900 font-mono shadow-2xs"
                  />
                  <LinkIcon className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                </div>
              </div>
            )}

            {/* Song Details Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Título de la Canción *
                </label>
                <input
                  type="text"
                  placeholder="ej: Perfect"
                  value={songTitle}
                  onChange={(e) => setSongTitle(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-amber-400 focus:outline-hidden text-slate-900 shadow-2xs"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Artista o Dedicado a
                </label>
                <input
                  type="text"
                  placeholder="ej: Artista / Para mi pareja"
                  value={songArtist}
                  onChange={(e) => setSongArtist(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-amber-400 focus:outline-hidden text-slate-900 shadow-2xs"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  ¿Quién sube o dedica la canción?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setUploader('novio')}
                    className={`py-2 px-3 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs ${
                      uploader === 'novio'
                        ? 'bg-blue-50 border-blue-300 text-blue-700 ring-2 ring-blue-200'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span>💙</span>
                    <span>{settings.partner1Name || 'Novio'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploader('novia')}
                    className={`py-2 px-3 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs ${
                      uploader === 'novia'
                        ? 'bg-rose-50 border-rose-300 text-rose-700 ring-2 ring-rose-200'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span>💖</span>
                    <span>{settings.partner2Name || 'Novia'}</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Duración aproximada
                </label>
                <input
                  type="text"
                  placeholder="ej: 3:45"
                  value={songDuration}
                  onChange={(e) => setSongDuration(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-amber-400 focus:outline-hidden text-slate-900 shadow-2xs"
                />
              </div>
            </div>

            {/* Special Note */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Dedicatoria o Nota Especial (Opcional)
              </label>
              <input
                type="text"
                placeholder="ej: Nuestra primera canción bailando juntos..."
                value={songNotes}
                onChange={(e) => setSongNotes(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-xl bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-amber-400 focus:outline-hidden text-slate-900 shadow-2xs"
              />
            </div>

            <button
              type="submit"
              disabled={isUploading || isProcessingFile || (activeTab === 'upload' && !selectedFile) || (activeTab === 'url' && !songUrl.trim())}
              className="w-full py-2.5 px-4 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold text-xs transition-all shadow-xs flex items-center justify-center gap-2 cursor-pointer"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Guardando en la Nube ({uploadProgress}%)...</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span>Guardar y Añadir a la Playlist</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Right Column: Preferences & Playback Modes */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-4">
              <Volume2 className="w-4 h-4 text-amber-500" />
              <span>Ajustes del Reproductor</span>
            </h3>

            <div className="space-y-4 text-xs">
              {/* Volume Slider */}
              <div>
                <div className="flex items-center justify-between text-slate-700 mb-1.5 font-bold">
                  <span>Volumen por defecto</span>
                  <span className="font-mono text-amber-700 font-bold bg-amber-50 px-2 py-0.5 rounded-lg border border-amber-200">
                    {Math.round((settings.musicVolume ?? 0.7) * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={settings.musicVolume ?? 0.7}
                  onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                  className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                />
              </div>

              {/* Loop Mode */}
              <div>
                <label className="block text-slate-700 mb-1.5 font-bold">
                  Modo de Reproducción
                </label>
                <div className="grid grid-cols-3 gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleLoopModeChange('all')}
                    className={`py-1.5 px-2 rounded-xl text-[11px] font-bold border transition-all cursor-pointer text-center shadow-2xs ${
                      (settings.musicLoopMode || 'all') === 'all'
                        ? 'bg-amber-50 border-amber-300 text-amber-800 ring-2 ring-amber-200'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Toda la lista
                  </button>
                  <button
                    type="button"
                    onClick={() => handleLoopModeChange('one')}
                    className={`py-1.5 px-2 rounded-xl text-[11px] font-bold border transition-all cursor-pointer text-center shadow-2xs ${
                      settings.musicLoopMode === 'one'
                        ? 'bg-amber-50 border-amber-300 text-amber-800 ring-2 ring-amber-200'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Repetir una
                  </button>
                  <button
                    type="button"
                    onClick={() => handleLoopModeChange('none')}
                    className={`py-1.5 px-2 rounded-xl text-[11px] font-bold border transition-all cursor-pointer text-center shadow-2xs ${
                      settings.musicLoopMode === 'none'
                        ? 'bg-amber-50 border-amber-300 text-amber-800 ring-2 ring-amber-200'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Sin bucle
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Real-time Cloud Guarantee Card */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-xs shadow-xs">
            <h4 className="font-bold text-amber-900 flex items-center gap-1.5 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-amber-600" />
              <span>Sincronización en la Nube Sin Pérdidas</span>
            </h4>
            <p className="text-[11px] text-slate-600 leading-relaxed">
              Las canciones subidas se guardan permanentemente en la base de datos de Firebase Firestore y en la memoria local del dispositivo. Se reproducen instantáneamente y sin retraso en el sitio web de ambos.
            </p>
          </div>
        </div>
      </div>

      {/* 3. List of Uploaded Songs */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
          <div className="flex items-center gap-2">
            <Music className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-bold text-slate-900">
              Playlist de la Pareja ({playlist.length})
            </h3>
          </div>
          <span className="text-[11px] text-slate-500 font-medium hidden sm:inline">
            Haz clic en la estrella para definir la canción que sonará por defecto.
          </span>
        </div>

        {playlist.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-xs bg-slate-50 rounded-2xl border border-dashed border-slate-200">
            No hay canciones registradas en la playlist. Sube una canción arriba para empezar.
          </div>
        ) : (
          <div className="space-y-2">
            {playlist.map((track, idx) => {
              const isActive = activeSongId === track.id;
              const isPreviewing = playingTrackId === track.id;

              return (
                <div
                  key={track.id || idx}
                  className={`p-3.5 rounded-2xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs ${
                    isActive
                      ? 'bg-amber-50/80 border-amber-300'
                      : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Play/Pause Preview Button */}
                    <button
                      type="button"
                      onClick={() => handleTogglePreview(track)}
                      className={`w-9 h-9 rounded-xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95 cursor-pointer shrink-0 shadow-xs ${
                        isPreviewing
                          ? 'bg-amber-500 text-white animate-pulse'
                          : 'bg-slate-100 border border-slate-200 text-slate-700 hover:bg-amber-100 hover:text-amber-800'
                      }`}
                      title={isPreviewing ? 'Pausar vista previa' : 'Escuchar vista previa'}
                    >
                      {isPreviewing ? (
                        <Pause className="w-4 h-4 fill-current" />
                      ) : (
                        <Play className="w-4 h-4 fill-current ml-0.5" />
                      )}
                    </button>

                    {/* Song Details */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-slate-900 truncate">
                          {track.title}
                        </span>
                        {isActive && (
                          <span className="px-2 py-0.5 rounded-full text-[9.5px] font-bold bg-amber-500 text-white flex items-center gap-1 shadow-2xs">
                            <Star className="w-2.5 h-2.5 fill-current" />
                            <span>Canción Activa</span>
                          </span>
                        )}
                        {track.uploadedBy && (
                          <span
                            className={`px-2 py-0.5 rounded-full text-[9.5px] font-bold ${
                              track.uploadedBy === 'novio'
                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : 'bg-rose-50 text-rose-700 border border-rose-200'
                            }`}
                          >
                            {track.uploadedBy === 'novio'
                              ? `💙 ${settings.partner1Name || 'Novio'}`
                              : `💖 ${settings.partner2Name || 'Novia'}`}
                          </span>
                        )}
                        {track.storageType === 'cloud_chunks' && (
                          <span className="px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] font-bold">
                            ☁️ Nube
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-0.5">
                        <span className="font-medium text-slate-700">{track.artist || 'Pareja'}</span>
                        {track.duration && (
                          <span className="flex items-center gap-0.5 font-mono">
                            <Clock className="w-2.5 h-2.5" />
                            {track.duration}
                          </span>
                        )}
                        {track.notes && (
                          <span className="italic text-slate-500 truncate max-w-xs">
                            "{track.notes}"
                          </span>
                        )}
                      </div>

                      {/* Linea de recorrido con barra blanca indicando lo reproducido */}
                      {isPreviewing && (
                        <div className="mt-2.5 pt-2 border-t border-amber-200/70 w-full max-w-md space-y-1">
                          <div className="relative flex items-center h-4 group cursor-pointer w-full">
                            {/* Pista base oscura para contraste */}
                            <div className="absolute inset-x-0 h-1.5 bg-slate-700/60 rounded-full overflow-hidden pointer-events-none border border-slate-600/30">
                              {/* Línea blanca brillante indicando lo reproducido */}
                              <div
                                className="h-full bg-white rounded-full shadow-[0_0_8px_rgba(255,255,255,0.95)] transition-[width] duration-75"
                                style={{
                                  width: `${
                                    previewDuration > 0
                                      ? Math.min(100, Math.max(0, (previewCurrentTime / previewDuration) * 100))
                                      : 0
                                  }%`,
                                }}
                              />
                            </div>
                            {/* Puntero / pin de arrastre blanco */}
                            <div
                              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)] border-2 border-amber-500 pointer-events-none transition-transform duration-75 group-hover:scale-125"
                              style={{
                                left: `${
                                  previewDuration > 0
                                    ? Math.min(100, Math.max(0, (previewCurrentTime / previewDuration) * 100))
                                    : 0
                                }%`,
                              }}
                            />
                            <input
                              type="range"
                              min="0"
                              max={previewDuration || 100}
                              step="0.25"
                              value={previewCurrentTime}
                              onChange={(e) => handleSeekPreview(parseFloat(e.target.value))}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                              aria-label="Progreso de la melodía"
                            />
                          </div>
                          <div className="flex items-center justify-between text-[10px] font-mono select-none px-0.5">
                            <span className="font-semibold text-slate-800">
                              {formatSeconds(previewCurrentTime)}
                            </span>
                            <span className="font-bold text-amber-700">
                              {formatSeconds(previewDuration || 0)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 sm:self-center self-end shrink-0">
                    {!isActive ? (
                      <button
                        type="button"
                        onClick={() => handleSetActiveSong(track.id)}
                        className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-amber-100 text-slate-700 hover:text-amber-900 text-xs font-bold transition-colors flex items-center gap-1 cursor-pointer border border-slate-200"
                        title="Hacer que esta canción suene en la web"
                      >
                        <Star className="w-3.5 h-3.5" />
                        <span>Activar</span>
                      </button>
                    ) : (
                      <span className="px-2.5 py-1.5 text-xs text-amber-700 font-bold flex items-center gap-1 bg-amber-100/60 rounded-xl border border-amber-200">
                        <Check className="w-3.5 h-3.5" />
                        <span>Sonando</span>
                      </span>
                    )}

                    <button
                      type="button"
                      onClick={() => handleDeleteSong(track.id)}
                      className="p-1.5 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer border border-transparent hover:border-rose-200"
                      title="Eliminar canción"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
